import React, { FC, LegacyRef } from 'react';
import * as XLSX from 'xlsx';
import momentTimezone from 'moment-timezone';
import { qrCodeGeneratorURL } from '~/helpers/constants';
import './FlyerTable.css';
import { IEventWithFlyer, IGatheringFlyer, IFlyerTableData } from '~/types/gathering';
import { ReactComponent as Community } from '~/utils/images/community.svg';
import useFlyerTableData from '~/hooks/building/flyer/useFlyerTableData';
import GenericModal from '~/components/GenericModal/GenericModal';
import { EditFlyerForm } from './EditFlyerForm/EditFlyerForm';
import { EmailFlyerForm } from './EmailFylerForm/EmailFlyerForm';
import { ISelectOption } from '~/helpers/interfaces';
import Spinner from '~/components/Spinner/Spinner';
import CobuTableAdvanced from '~/components/CobuTableAdvanced/CobuTableAdvanced';
import { ReactComponent as Search } from '~/utils/images/search.svg';

interface IProps {
  showCompletedReports: boolean;
  paginatedData: IFlyerTableData;
}

const FlyerTable: FC<IProps> = ({ showCompletedReports, paginatedData }) => {
  const {
    items,
    pagination,
    isLoading,
    isFetching,
    isError,
    refetch,
    search,
    handleSearchChange,
    clearSearch,
    page,
    goToPage,
    nextPage,
    prevPage,
  } = paginatedData;

  const {
    columns,
    editFlyerData,
    onEditFlyerCancel,
    emailFlyerLoader,
    emailFlyerData,
    onEmailFlyerSubmit,
    onEmailModalCancel,
    flyerTemplatOptions,
    templateSelectionOption,
    setTemplateSelectionOption,
    pdfFlyerHtmlContent,
    instaFlyerHtmlContent,
    pdfIFrameRef,
    instaIFrameRef,
    loading,
    downloadAttachments,
    refetchFlyers,
    byIdflyerGroupEmails,
    byBuildingIdFlyerNotes,
  } = useFlyerTableData({
    eventsWithFlyer: items,
    refetchFlyers: () => refetch(),
    completed: paginatedData.completed,
    updateItemInCache: paginatedData.updateItemInCache,
    removeItemFromCache: paginatedData.removeItemFromCache,
  });

  const onDownloadXlsx = () => {
    const rows = (items || []).map((e) => {
      const flyer = e.flyers?.[0];
      const tz = e.buildingTimezone;
      const createdAt = tz
        ? momentTimezone.tz(e.createdAt, tz).format('MM/DD/YYYY')
        : momentTimezone(e.createdAt).format('MM/DD/YYYY');
      const eventDate = tz
        ? momentTimezone.tz(e.startTime, tz).format('MM/DD/YYYY')
        : momentTimezone(e.startTime).format('MM/DD/YYYY');
      const eventTime = tz
        ? momentTimezone.tz(e.startTime, tz).format('hh:mm A')
        : momentTimezone(e.startTime).format('hh:mm A');

        const groupEmails = (byIdflyerGroupEmails?.[e.buildingUuid] || []) as string[];
        const combinedEmails = groupEmails.join(',');

      const logoUrl =
        (e.propertyLogoFlyerUrl as string | undefined) ||
        (e.propertyLogoUrl as string | undefined) ||
        (flyer?.flyerLogo as string | undefined) ||
        '';
      const qrUrl = e.onboardingDeeplink
        ? qrCodeGeneratorURL(encodeURIComponent(e.onboardingDeeplink))
        : '';

      let cleanedDescription = e.description || '';
      cleanedDescription = cleanedDescription.replace(
        /\n*\s*Please\s+(?:RSVP|express\s+interest)\s*-\s*it\s+helps\s+us\s+plan\s+better!\s*Plus,\s+you['\u2019]ll\s+get\s+reminders\.?\s*$/i,
        ''
      );
      cleanedDescription = cleanedDescription.replace(/[\n\r]+/g, ' ');
      cleanedDescription = cleanedDescription.replace(/\s+/g, ' ').trim();

      const buildingNotes = byBuildingIdFlyerNotes?.[e.buildingUuid] || [];
      const pinnedNotes = buildingNotes.filter((note: any) => note.pinned);
      const notesText = pinnedNotes
        .map((note: any) => `- ${note.note}`)
        .join('\n');

        return {
          'BUILDING': e.buildingName,
          'CREATE DATE': createdAt,
          'DATE OF EVENT': eventDate,
          'TIME OF EVENT': eventTime,
          'LOCATION': e.locationString,
          'EVENT TITLE': e.title,
          'EVENT DESCRIPTION': cleanedDescription,
          'CREATOR NAME': e.creatorName,
          'CREATOR EMAIL': e.creatorEmail,
          'RSVP': e.isRSVP ? 'true' : 'false',
          'LEFT SPOTS': e.leftSpots ?? '',
          'STATUS': e.flyerStatus,
          'BUILDING CODE': flyer?.flyerDetails?.propertyCode || '',
          'BUILDING LOGO': logoUrl,
          'QR CODE': qrUrl,
          'FLYER EMAILS': combinedEmails,
          'NOTES': notesText
        };
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Flyers');
      XLSX.writeFile(wb, 'flyers.xlsx');
    };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const { totalPages } = pagination;
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [1];
    if (page > 3) pages.push('...');

    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);

    return pages;
  };

  if (isLoading && items.length === 0) {
    return <Spinner />;
  }

  if (isError) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Community />
        <h2>Failed to load flyers</h2>
        <p style={{ color: '#666', marginBottom: '16px' }}>
          Something went wrong while fetching flyer data.
        </p>
        <button
          className='more-actions-btn pointer'
          onClick={() => refetch()}
          style={{ padding: '8px 24px' }}
        >
          Retry
        </button>
      </div>
    );
  }

  const isPdfFlyer = editFlyerData?.flyer?.flyerTemplateType === 'PDF Flyer';

  return (
    <div>
      {(loading || isFetching) && <Spinner />}

      {/* Search + Actions toolbar */}
      <div className='flyer-toolbar'>
        <div className='flyer-search-container'>
          <Search className='flyer-search-icon' />
          <input
            className='flyer-search-input'
            placeholder='Search by Building Name'
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {search && (
            <button
              className='flyer-search-clear'
              onClick={clearSearch}
              aria-label='Clear search'
            >
              &times;
            </button>
          )}
        </div>
        <div className='more-actions-btn pointer' onClick={onDownloadXlsx}>
          Download XLSX
        </div>
      </div>

      {/* Results info */}
      <div className='flyer-results-info'>
        {pagination.total > 0 ? (
          <span>
            Showing {(page - 1) * pagination.limit + 1}
            {' - '}
            {Math.min(page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} flyers
          </span>
        ) : (
          !isFetching && <span>No results</span>
        )}
      </div>

      {/* Table or empty state */}
      {items.length > 0 ? (
        <CobuTableAdvanced
          centerAlignedHeaderAndContent
          noData={{
            label: 'No Data',
            action: undefined,
            image: undefined,
          }}
          columns={columns}
          data={items}
          sort={{
            id: 'startTime',
            descending: true,
          }}
          actionsStyle={{ top: '125px', right: '15vw' }}
        />
      ) : (
        !isFetching && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Community />
            <h2>No Flyers found!</h2>
            {search && (
              <p style={{ color: '#666' }}>
                No results for &quot;{search}&quot;. Try a different search
                term.
              </p>
            )}
          </div>
        )
      )}

      {/* Pagination controls */}
      {pagination.totalPages > 1 && (
        <div className='flyer-pagination'>
          <button
            className='flyer-pagination-btn'
            disabled={page <= 1}
            onClick={prevPage}
          >
            &laquo; Previous
          </button>

          <div className='flyer-pagination-pages'>
            {getPageNumbers().map((pageNum, idx) =>
              typeof pageNum === 'string' ? (
                <span key={`ellipsis-${idx}`} className='flyer-pagination-ellipsis'>
                  ...
                </span>
              ) : (
                <button
                  key={pageNum}
                  className={`flyer-pagination-page ${
                    pageNum === page ? 'flyer-pagination-page-active' : ''
                  }`}
                  onClick={() => goToPage(pageNum)}
                >
                  {pageNum}
                </button>
              )
            )}
          </div>

          <button
            className='flyer-pagination-btn'
            disabled={!pagination.hasMore}
            onClick={nextPage}
          >
            Next &raquo;
          </button>
        </div>
      )}

      {/* Modals */}
      <GenericModal
        showModal={!!editFlyerData.flyer}
        title={`Edit ${isPdfFlyer ? 'PDF' : 'Instagram'} Flyer`}
        content={
          editFlyerData.flyer ? (
            <EditFlyerForm
              flyerTemplatOptions={flyerTemplatOptions as ISelectOption[]}
              flyerData={editFlyerData.flyer as IGatheringFlyer}
              eventData={editFlyerData.event as IEventWithFlyer}
              onCancel={onEditFlyerCancel}
              fetchEventsWithFlyers={refetchFlyers}
            />
          ) : null
        }
        enableOverflowScrolls
        hideActionButton
        canceActionButton
        actionText={'Submit'}
        actionFunction={() => {}}
        onChange={() => {}}
      />
      <GenericModal
        showModal={!!emailFlyerData.event}
        title={'Email flyers'}
        enableOverflowScrolls
        content={
          emailFlyerData.event ? (
            <EmailFlyerForm
              templateSelectionOption={templateSelectionOption}
              setTemplateSelectionOption={setTemplateSelectionOption}
              loader={emailFlyerLoader}
              eventData={emailFlyerData.event as IEventWithFlyer}
              flyerGroupEmails={emailFlyerData.flyerGroupEmails}
              onSubmitForm={onEmailFlyerSubmit}
              onCancel={onEmailModalCancel}
              downloadAttachments={downloadAttachments}
            />
          ) : null
        }
        hideActionButton
        canceActionButton
        actionText={'Submit'}
        actionFunction={() => {}}
        onChange={() => {}}
      />
      {pdfFlyerHtmlContent && (
        <iframe
          id='pdfIFrameRef'
          ref={pdfIFrameRef as LegacyRef<HTMLIFrameElement>}
          style={{ width: '100%', height: '1056px', visibility: 'hidden' }}
          srcDoc={pdfFlyerHtmlContent}
        />
      )}
      {instaFlyerHtmlContent && (
        <iframe
          id='instaIFrameRef'
          ref={instaIFrameRef as LegacyRef<HTMLIFrameElement>}
          style={{ width: '1080px', height: '1080px', visibility: 'hidden' }}
          srcDoc={instaFlyerHtmlContent}
        />
      )}
    </div>
  );
};

export default React.memo(FlyerTable);
